/**
 * Manager My team in the employee portal. Mirrors the backend `my-team`
 * module (`employee/my-team/*`). Every shape carries only the v1 Manager
 * permission set (decision D4): work details, leave dates, leave types and
 * balances. Never notes, compensation, banking, family, documents, personal
 * email or leave reasons.
 */
import type { ApiResponse } from './generic';
import type { PaginationMeta } from './common';
import type { LeaveBalanceCheck, TeamOverlap } from './leave-management';

export enum MyTeamDepth {
  /** The manager's whole reporting subtree. */
  All = 'all',
  /** Only people who report to the manager directly. */
  Direct = 'direct',
}

export interface MyTeamRef {
  id: string;
  name: string;
}

export interface MyTeamManagerRef {
  employeeId: string;
  firstName: string;
  lastName: string | null;
}

export interface MyTeamMember {
  employeeId: string;
  firstName: string;
  lastName: string | null;
  profileImage: string | null;
  designation: MyTeamRef | null;
  department: MyTeamRef | null;
  branch: MyTeamRef | null;
  workEmail: string | null;
  workPhone: string | null;
  /** Start date (`yyyy-MM-dd`). */
  dateOfJoining: string | null;
  employmentType: string | null;
  manager: MyTeamManagerRef | null;
  isDirectReport: boolean;
  /**
   * Their current reporting line is temporary: for example the report of a
   * manager who left, covered until HR moves them to a permanent manager.
   */
  isTemporary: boolean;
  /** Set for someone who has not started yet: their first day. */
  startsOn: string | null;
  onLeaveToday: boolean;
}

export interface MyTeamMemberDetail extends MyTeamMember {
  directReportCount: number;
  teamSize: number;
}

export interface MyTeamSummary {
  people: number;
  directReports: number;
  outToday: number;
  startingSoon: number;
  /** Leave requests waiting for this manager (same as the Approvals tab). */
  pendingApprovals: number;
}

export interface MyTeamAccess {
  /** The company turned manager access on. */
  enabled: boolean;
  /** The signed-in employee has at least one active report. */
  isManager: boolean;
  /**
   * Covering someone's approvals right now (an out-of-office delegation).
   * Opens the Approvals inbox only, never the team (decision D10).
   */
  isDelegate: boolean;
  teamSize: number;
  directReports: number;
  permissions: {
    leaveApprove: boolean;
    timesheetRead: boolean;
  };
}

export interface MyTeamLeaveBalance {
  walletId: string;
  leaveType: MyTeamRef;
  policyName: string | null;
  /** `balance`, or `record_only`: an absence with no balance to show. */
  tracking?: string;
  available: number;
  taken: number;
  availableInHours: number;
  takenInHours: number;
  hoursPerDay: number;
  walletDisplayUnit: string;
}

export interface MyTeamLeaveRequest {
  id: string;
  leaveType: MyTeamRef;
  startDate: string;
  endDate: string;
  startSession: string | null;
  endSession: string | null;
  startTime: string | null;
  endTime: string | null;
  days: number;
  status: string;
  createdAt: string;
}

/** One day's leave in the team calendar: no reason and no email. */
export interface MyTeamCalendarLeave {
  leaveRequestId: string;
  startDate: string;
  endDate: string;
  status: string;
  startSession: string | null;
  endSession: string | null;
  startTime: string | null;
  endTime: string | null;
  leaveType: MyTeamRef;
  employee: {
    id: string;
    firstName: string;
    lastName: string | null;
    profileImage: string | null;
  };
}

export interface MyTeamListParams {
  page?: number;
  limit?: number;
  search?: string;
  depth?: MyTeamDepth;
  branchId?: string;
  departmentId?: string;
}

export interface MyTeamCalendarParams {
  startDate: string;
  endDate: string;
  employeeIds?: string;
  leaveTypeIds?: string;
  depth?: MyTeamDepth;
}

export interface MyTeamLeaveRequestsParams {
  page?: number;
  limit?: number;
  status?: string;
  upcoming?: boolean;
}

/** A teammate already away during a request's dates: names and dates only. */
export interface MyTeamTeamAbsence {
  name: string;
  startDate: string;
  endDate: string;
}

/**
 * A pending leave request the signed-in approver may decide (reporting
 * manager or delegate): only what a decision needs (D10).
 */
export interface MyTeamApprovalRequest {
  id: string;
  employeeId: string | null;
  employeeName: string;
  profileImage: string | null;
  jobTitle: string | null;
  leaveType: string | null;
  /** Remaining balance of this leave type, in days. */
  balance: number | null;
  startDate: string;
  endDate: string;
  startSession: string | null;
  endSession: string | null;
  startTime: string | null;
  endTime: string | null;
  days: number;
  reason: string | null;
  routingStep: number;
  /** Set when the approver is covering for someone: that person's name. */
  onBehalfOf: string | null;
  /**
   * Reached this manager by escalation: it waited N business days per level
   * on a manager below (JP, 2026-10-11). Older servers: unset.
   */
  escalated?: boolean;
  /** The manager below it still waits on, when escalated. */
  escalatedFrom?: string | null;
  teamAway: MyTeamTeamAbsence[];
  /** Who on the team is off on those dates (shown above Approve / Decline). */
  teamOverlap?: TeamOverlap;
  createdAt: string;
  /** The balance check at decision time (null when there is no wallet). */
  balanceCheck?: LeaveBalanceCheck | null;
  /** Whole days since it was submitted. */
  pendingDays?: number;
  /** Older than the company's overdue threshold. */
  overdue?: boolean;
}

/** An out-of-office window the signed-in employee is covering right now. */
export interface MyTeamCoveringWindow {
  delegationId: string;
  delegatorName: string;
  requestId: string | null;
  startsAt: string;
  endsAt: string;
}

/** One of the manager's own out-of-office delegations. */
export interface MyTeamDelegation {
  id: string;
  delegateEmployeeId: string | null;
  delegateName: string;
  delegateJobTitle: string | null;
  delegateProfileImage: string | null;
  startsAt: string;
  endsAt: string;
  /** Already in effect (the delegate decides for the manager now). */
  active: boolean;
}

export interface MyTeamDelegateCandidate {
  employeeId: string;
  name: string;
  jobTitle: string | null;
  profileImage: string | null;
}

export interface CreateMyTeamDelegationPayload {
  delegateEmployeeId: string;
  /** ISO timestamps. */
  startsAt: string;
  endsAt: string;
}

export enum MyTeamDecision {
  Approve = 'approve',
  Reject = 'reject',
}

/** `errorCode` of the 409s a decision can meet. */
export enum MyTeamApprovalConflict {
  OwnerChanged = 'approvals.ownerChanged',
  AlreadyDecided = 'approvals.alreadyDecided',
}

export type MyTeamAccessResponse = ApiResponse<MyTeamAccess>;
export type MyTeamApprovalsResponse = ApiResponse<MyTeamApprovalRequest[]>;
/** Who a timesheet approval is about: name, photo and job title only (D4). */
export interface MyTeamTimesheetPerson {
  id: string;
  name: string;
  profileImage: string | null;
  designation: string | null;
}

interface MyTeamTimesheetApprovalBase {
  id: string;
  employee: MyTeamTimesheetPerson;
  /** Reached me because it waited on a manager below (escalation). */
  escalated: boolean;
  /** Decided as someone's delegate: whose approval it is. */
  onBehalfOf: string | null;
  /** Approving hands it to HR for the final approval (manager then HR). */
  hrNext: boolean;
  createdAt: string;
}

/** A week waiting for the manager (`week_approval`). */
export interface MyTeamWeekApproval extends MyTeamTimesheetApprovalBase {
  weekStart: string;
  weekEnd: string;
  totalWorkedMin: number;
  totalWorkableMin: number;
  days: {
    workDate: string;
    dayType: string;
    status: string;
    workedMin: number;
    expectedMin: number;
  }[];
}

/** A change asked on one day (`timesheet_request`). */
export interface MyTeamTimesheetChange extends MyTeamTimesheetApprovalBase {
  workDate: string;
  reason: string | null;
  proposed: {
    resultSegments?: {
      type: 'CLOCK' | 'BREAK';
      startTime?: string;
      endTime?: string | null;
      employeeNote?: string | null;
    }[];
  };
  /** The day changed since it was asked: approving it is refused (409). */
  isStale: boolean;
}

/** `GET employee/my-team/approvals/timesheets`. */
export interface MyTeamTimesheetApprovals {
  weeks: MyTeamWeekApproval[];
  requests: MyTeamTimesheetChange[];
}

export type MyTeamTimesheetApprovalKind = 'weeks' | 'requests';

export type MyTeamTimesheetApprovalsResponse =
  ApiResponse<MyTeamTimesheetApprovals>;

export type MyTeamApprovalsCountResponse = ApiResponse<{ pending: number }>;
export type MyTeamCoveringResponse = ApiResponse<MyTeamCoveringWindow[]>;
export type MyTeamDelegationsResponse = ApiResponse<MyTeamDelegation[]>;
export type MyTeamDelegateCandidatesResponse = ApiResponse<
  MyTeamDelegateCandidate[]
>;
export type MyTeamSummaryResponse = ApiResponse<MyTeamSummary>;
export type MyTeamListResponse = ApiResponse<{
  data: MyTeamMember[];
  meta: PaginationMeta;
}>;
export type MyTeamMemberResponse = ApiResponse<MyTeamMemberDetail>;
export type MyTeamLeaveBalancesResponse = ApiResponse<MyTeamLeaveBalance[]>;
export type MyTeamLeaveRequestsResponse = ApiResponse<{
  data: MyTeamLeaveRequest[];
  meta: PaginationMeta;
}>;
export type MyTeamCalendarResponse = ApiResponse<{
  startDate: string;
  endDate: string;
  holidays: { date: string; name: string; description?: string | null }[];
  leaves: Record<string, MyTeamCalendarLeave[]>;
}>;

/*
 * My team for every employee (`employee/team/*`, sprint spec 3.2, decision
 * D49), manager or not. Work details and approved leave dates only: never a
 * leave type, reason, balance, personal email or HR record (D20).
 */

/** Someone's current or next approved leave. */
export interface TeamAway {
  /** `yyyy-MM-dd`. */
  startDate: string;
  /** `yyyy-MM-dd`. */
  endDate: string;
  /** Part of a day only: a half day or an hourly leave. */
  halfDay: boolean;
}

export interface TeamPerson {
  employeeId: string;
  firstName: string;
  lastName: string | null;
  avatarUrl: string | null;
  jobTitle: string | null;
  department: string | null;
  workEmail: string | null;
  /** The reporting line tying this person to the viewer is temporary. */
  isTemporary: boolean;
  awayToday: boolean;
  nextAway: TeamAway | null;
}

export interface TeamOverview {
  manager: TeamPerson | null;
  /** Everyone else who reports to my current manager. */
  peers: TeamPerson[];
  directReports: TeamPerson[];
  isManager: boolean;
  isDelegate: boolean;
  subtreeSize: number;
}

export type TeamRelation =
  'me' | 'manager' | 'peer' | 'direct_report' | 'coworker';

/** One approved leave in "Who's off": names and dates, never the type. */
export interface TeamTimeOffRow {
  employeeId: string;
  firstName: string;
  lastName: string | null;
  avatarUrl: string | null;
  relation: TeamRelation;
  startDate: string;
  endDate: string;
  startSession: string | null;
  endSession: string | null;
  /** Hourly leave only (`HH:mm`). */
  startTime: string | null;
  endTime: string | null;
  label: string;
}

export interface TeamTimeOff {
  /**
   * `team`: me, my manager, my peers and my direct reports. `company`: every
   * active coworker, when I have neither a manager nor direct reports.
   */
  scope: 'team' | 'company';
  from: string;
  to: string;
  rows: TeamTimeOffRow[];
}

export interface TeamTimeOffParams {
  /** `yyyy-MM-dd`; defaults to today (company time zone). */
  from?: string;
  /** `yyyy-MM-dd`; at most 92 days after `from`. */
  to?: string;
}

export type TeamOverviewResponse = ApiResponse<TeamOverview>;
export type TeamTimeOffResponse = ApiResponse<TeamTimeOff>;

/**
 * `GET employee/my-team/members/:employeeId/probation` (2026-10-11): the
 * member page's probation card. A note or reason comes back to its author
 * only.
 */
export interface MyTeamMemberProbation {
  enabled: boolean;
  status: string | null;
  /** YYYY-MM-DD. */
  endDate: string | null;
  /** Active or extended: still waiting for a decision. */
  undecided: boolean;
  /** The manager confirms or extends directly (else: recommends to HR). */
  canDecide: boolean;
  /** The employee works in Quebec. */
  quebec: boolean;
  /** Quebec only: when 3 months of uninterrupted service are reached. */
  noticeDate: string | null;
  recommendation: {
    /** `end` comes from a probation evaluation only (V2-6). */
    type: 'confirm' | 'extend' | 'end';
    proposedEndDate: string | null;
    createdAt: string;
    byMe: boolean;
    managerName: string | null;
    reason: string | null;
  } | null;
  discussion: {
    createdAt: string;
    byMe: boolean;
    managerName: string | null;
    note: string | null;
  } | null;
}

/** What a manager's probation action did. */
export interface MyTeamProbationResult {
  outcome: 'decided' | 'recommended' | 'discussion_requested';
  notified: number;
}

export type MyTeamProbationAction = 'confirm' | 'extend' | 'discuss';

export type MyTeamProbationPayload =
  | { action: 'confirm' }
  | { action: 'extend'; newEndDate: string; reason: string }
  | { action: 'discuss'; note?: string };

export type MyTeamMemberProbationResponse = ApiResponse<MyTeamMemberProbation>;
export type MyTeamProbationResultResponse = ApiResponse<MyTeamProbationResult>;
